import { Stack, Switch, Typography, useTheme } from "@mui/material";
import { useAtomValue } from "jotai";
import type { FC } from "react";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { LanguageDrawer } from "web_myplann/Profile/Components/LanguageDrawer";
import { SettingsRow } from "web_myplann/Profile/Components/SettingsRow";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useBiometryToggle } from "web_myplann/Shared/Hooks/useBiometryToggle";
import { useDarkModeToggle } from "web_myplann/Shared/Hooks/useDarkModeToggle";
import { useLanguagePicker } from "web_myplann/Shared/Hooks/useLanguagePicker";
import { useLogOut } from "web_myplann/Shared/Hooks/useLogOut";
import { appInfoState } from "web_myplann/Shared/State/appInfoAtom";

export const SettingsPage: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const appInfoAtom = useAtomValue(appInfoState);
	const { logOut, removeAccount } = useLogOut();
	const { isDarkMode, toggle: toggleDarkMode } = useDarkModeToggle();
	const {
		hasBiometry,
		toggle: toggleBiometry,
		isPending: isBiometryPending,
	} = useBiometryToggle();
	const {
		isOpen: isLanguageOpen,
		open: openLanguage,
		close: closeLanguage,
		select: selectLanguage,
	} = useLanguagePicker();

	return (
		<>
			<MyPlannLayout
				disableContentPadding
				topBar={
					<NavigationHeader
						title={t(Resources.Settings.title)}
						defaultRoute={Routing.Dashboard.path()}
					/>
				}
			>
				<Stack p={1.6} gap={0.8}>
					<SettingsRow
						label={t(Resources.Settings.DarkMode)}
						rightSlot={
							<Switch checked={isDarkMode} onChange={toggleDarkMode} />
						}
					/>
					<SettingsRow
						label={t(Resources.Security.Biometry)}
						rightSlot={
							<Switch
								checked={hasBiometry}
								onChange={toggleBiometry}
								disabled={isBiometryPending}
							/>
						}
					/>
					<SettingsRow
						label={t(Resources.Settings.Language)}
						showChevron
						onClick={openLanguage}
					/>
					<SettingsRow
						label={t(Resources.Settings.LogOut)}
						onClick={logOut}
						destructive
					/>
					<SettingsRow
						label={t(Resources.Settings.RemoveAccount)}
						onClick={removeAccount}
						destructive
						isLast
					/>

					<Stack direction={"row"} justifyContent={"center"} mt={1}>
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[500]}
						>
							{`${t(Resources.Settings.AppVersion)} ${appInfoAtom.appVersion}`}
						</Typography>
					</Stack>
				</Stack>
			</MyPlannLayout>

			<LanguageDrawer
				open={isLanguageOpen}
				onClose={closeLanguage}
				onSelect={selectLanguage}
			/>
		</>
	);
};
